// src/V2/helpers/international.ts
import store from "@/V2/store/index"
import axios from 'axios';
import Vue from 'vue';
import { getSessionToken } from '@shopify/app-bridge-utils';

type CacheValue = {
    country_of_origin: string | null;
    hs_code: string | null;
    goods_description: string | null;
};
// Tune if needed
const BATCH_SIZE = 250;

function getVariantBatchQuery(
    variantNamespace?: string,
    variantKey?: string,
    productNamespace?: string,
    productKey?: string
) {
    const variantMetafield =
        variantNamespace && variantKey
            ? `
                variantMetafield: metafield(
                    namespace: "${variantNamespace}"
                    key: "${variantKey}"
                ) {
                    value
                }
              `
            : '';

    const productMetafield =
        productNamespace && productKey
            ? `
                productMetafield: metafield(
                    namespace: "${productNamespace}"
                    key: "${productKey}"
                ) {
                    value
                }
              `
            : '';

    return `
    query VariantBatch($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on ProductVariant {
          id

          inventoryItem {
            countryCodeOfOrigin
            harmonizedSystemCode
            countryHarmonizedSystemCodes(first: 250) {
              edges {
                node {
                  countryCode
                  harmonizedSystemCode
                }
              }
            }
          }

          ${variantMetafield}

          product {
            id
            ${productMetafield}
          }
        }
      }
    }`;
}

function collectVariantNeeds(orders: any[]) {
  // Map<variantId, Set<countryCode>>
  const needsByVariant = new Map<string, Set<string>>();
  for (const order of orders || []) {
    const shipCountry: string | null = order?.shipping_address?.country_code || null;
    for (const li of order?.line_items || []) {
      const id = li.variant_id;
      if (!id) continue;
      const key = String(id);
      if (!needsByVariant.has(key)) needsByVariant.set(key, new Set());
      if (shipCountry) needsByVariant.get(key)!.add(shipCountry);
    }
  }
  return needsByVariant;
}

function pickVariantsToFetch(
  needsByVariant: Map<string, Set<string>>,
  cache: Record<string, CacheValue>
) {
  const toFetch: string[] = [];
  for (const [variantId, countries] of needsByVariant) {
    const globalKey = `${variantId}:GLOBAL`;
    let need = !cache[globalKey]; // if no GLOBAL, fetch
    if (!need) {
      for (const cc of countries) {
        if (!cache[`${variantId}:${cc}`]) { need = true; break; }
      }
    }
    if (need) toFetch.push(variantId);
  }
  return toFetch;
}

async function fetchAndWarmVariants(
  variantIds: string[],
    app: any,
    cache: Record<string, CacheValue>,
    variantNamespace?: string,
    variantKey?: string,
    productNamespace?: string,
    productKey?: string
) {
  const VARIANT_BATCH_QUERY = getVariantBatchQuery(variantNamespace, variantKey, productNamespace, productKey);
  for (let i = 0; i < variantIds.length; i += BATCH_SIZE) {
    const chunk = variantIds.slice(i, i + BATCH_SIZE);
    const ids = chunk.map(id => `gid://shopify/ProductVariant/${id}`);

    const token = await getSessionToken(app);
    const resp = await axios.post(
      `${process.env.VUE_APP_V2_SHOPIFY_API}/graphql`,
      { graphql: { query: VARIANT_BATCH_QUERY, variables: { ids } } },
      { headers: { Authorization: token } }
    );
    store.commit('set_loading', { status: true, message: 'Retrieving international data', step: 'International', count: variantIds.length - i}, {root: true })

    const nodes = resp.data?.data?.nodes || [];
    for (const node of nodes) {
      if (!node?.inventoryItem) continue;

      const variantGid: string = node.id; // gid://shopify/ProductVariant/123
      const variantId = variantGid.split('/').pop()!;
      const inv = node.inventoryItem;
      const product = node.product ?? {};

      const baseOrigin = inv.countryCodeOfOrigin ?? null;
      const globalHS   = inv.harmonizedSystemCode ?? null;
      const goodsDescription =
          node.variantMetafield?.value ||
          product.productMetafield?.value ||
          null;
      // GLOBAL cache
      Vue.set(cache, `${variantId}:GLOBAL`, {
        country_of_origin: baseOrigin,
        hs_code: globalHS,
        goods_description: goodsDescription
      });

      // Country overrides
      const edges = inv.countryHarmonizedSystemCodes?.edges ?? [];
      for (const e of edges) {
        const cc  = e?.node?.countryCode;
        const hsc = e?.node?.harmonizedSystemCode ?? null;
        if (cc) {
          Vue.set(cache, `${variantId}:${cc}`, {
            country_of_origin: baseOrigin,
            hs_code: hsc,
            goods_description: goodsDescription
          });
        }
      }
      
    }

    // (Optional) throttle slightly
    // await new Promise(r => setTimeout(r, 120));
  }
}

/**
 * Public helper:
 * - orders: list of orders (already filtered to international if you want)
 * - app: rootGetters['Shopify/app']
 * - cache: your reactive cache object (e.g. state.international_data_cache)
 */
export async function hydrateInternationalDataForOrders(
  orders: any[],
  app: any,
  variant_goods_description_metafield: any,
  product_goods_description_metafield: any,
  cache: Record<string, CacheValue>
): Promise<boolean> {
  
  let variantNamespace;
  let variantKey;

  if (variant_goods_description_metafield) {
      [variantNamespace, variantKey] =
          variant_goods_description_metafield.split('.', 2);
  }

  let productNamespace;
  let productKey;

  if (product_goods_description_metafield) {
      [productNamespace, productKey] =
          product_goods_description_metafield.split('.', 2);
  }
  
  if (!orders?.length) return true;

  // 1) Collect all needs from orders
  const needsByVariant = collectVariantNeeds(orders);
  // 2) Identify variants we still need to fetch
  const toFetch = pickVariantsToFetch(needsByVariant, cache);
  store.commit('set_loading', { status: true, message: 'Retrieving international data', step: 'International', count: toFetch.length }, {root: true })

  // 3) Fetch variants in batches and warm the cache (GLOBAL + overrides)
  if (toFetch.length) {
    await fetchAndWarmVariants(toFetch, app, cache, variantNamespace, variantKey, productNamespace, productKey);
  }

  // 4) Apply from cache to each line item
  for (const order of orders || []) {
    const shipCountry = order?.shipping_address?.country_code || null;
    for (const li of order?.line_items || []) {
      const id = li.variant_id;
      if (!id) continue;
      const best =
        (shipCountry && cache[`${id}:${shipCountry}`]) ||
        cache[`${id}:GLOBAL`] ||
        null;
      if (best) {
        Vue.set(li, 'country_of_origin', best.country_of_origin);
        Vue.set(li, 'hs_code', best.hs_code);
        Vue.set(li, 'goods_description', best.goods_description);
      }
    }
  }

  return true;
}
